import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Bed, Bath, Maximize } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { formatPrice } from "../lib/api";

export default function PropertyCard({ p }) {
    const { t, lang } = useI18n();
    const title = lang === "en" ? p.title_en || p.title_fr : p.title_fr;
    const isRent = p.transaction === "rent";
    const cover = p.images?.[0] || "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=900";
    return (
        <Link
            to={`/biens/${p.id}`}
            data-testid={`property-card-${p.id}`}
            className="group block bg-white border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_18px_40px_rgb(0,0,0,0.10)] hover:-translate-y-1 transition-all duration-300 overflow-hidden"
        >
            <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                <img
                    src={cover}
                    alt={title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                />
                <span
                    className={`absolute top-3 left-3 px-3 py-1 text-[0.62rem] tracking-[0.22em] uppercase font-bold ${
                        isRent ? "bg-brand-emerald text-white" : "bg-brand-gold text-white"
                    }`}
                >
                    {isRent ? t("featured.for_rent") : t("featured.for_sale")}
                </span>
            </div>
            <div className="p-5">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                    <MapPin className="w-3.5 h-3.5 text-brand-emerald" />
                    <span>
                        {p.city}
                        {p.neighborhood ? `, ${p.neighborhood}` : ""}
                    </span>
                </div>
                <h3 className="font-display text-lg leading-snug text-brand-navy line-clamp-2 min-h-[3.4rem]">
                    {title}
                </h3>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                    {p.surface ? (
                        <span className="inline-flex items-center gap-1">
                            <Maximize className="w-3.5 h-3.5 text-brand-gold" />
                            {p.surface} m²
                        </span>
                    ) : null}
                    {p.bedrooms ? (
                        <span className="inline-flex items-center gap-1">
                            <Bed className="w-3.5 h-3.5 text-brand-gold" />
                            {p.bedrooms}
                        </span>
                    ) : null}
                    {p.bathrooms ? (
                        <span className="inline-flex items-center gap-1">
                            <Bath className="w-3.5 h-3.5 text-brand-gold" />
                            {p.bathrooms}
                        </span>
                    ) : null}
                </div>
                <div className="mt-4 flex items-end justify-between">
                    <div className="text-brand-emerald font-bold tracking-tight text-lg">
                        {formatPrice(p.price, p.currency)}
                        {isRent ? (
                            <span className="text-xs text-gray-500 font-medium">
                                {" "}
                                {t("featured.per_month")}
                            </span>
                        ) : null}
                    </div>
                    <span className="text-[0.7rem] tracking-[0.22em] uppercase font-semibold text-brand-navy group-hover:text-brand-gold transition-colors">
                        {t("featured.see_details")} →
                    </span>
                </div>
            </div>
        </Link>
    );
}
